import { type FunkitTheme, toRestyleTheme } from './toRestyleTheme'
import { type FunkitTokens, darkTokens, lightTokens } from './tokens'

/** Recursive partial — every group/leaf of the nested token tree is optional. */
type DeepPartial<T> = T extends object
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : T

/**
 * A deep-partial override of the nested Fun Variables {@link FunkitTokens}
 * groups. Pass to {@link lightTheme} / {@link darkTheme} to restyle only the
 * leaves a brand changes (`background` / `content` / `border` / `brand` / …);
 * anything omitted keeps the base token's value.
 */
export type FunkitTokensOverride = DeepPartial<FunkitTokens>

/** A mergeable plain object — not null, not an array. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Deep-merge `override` onto a deep *clone* of `base`. The result shares no
 * object references with `base`, so the built-in tokens are never aliased into a
 * theme — and thus can't be mutated through one. Non-object values (and arrays)
 * replace the base value whole.
 */
function deepMerge<T>(base: T, override?: DeepPartial<T>): T {
  if (!isPlainObject(base)) {
    return override === undefined ? base : (override as T)
  }
  const source: Record<string, unknown> = isPlainObject(override)
    ? override
    : {}
  const cloned: Record<string, unknown> = {}
  for (const key of Object.keys(base)) {
    cloned[key] = deepMerge(base[key], source[key])
  }
  return cloned as T
}

/**
 * Build a light {@link FunkitTheme}, deep-merging an optional brand override
 * onto the SDK's `lightTokens`. A brand specifies only the leaves it changes —
 * the design defines `default` / `pressed` states — and everything else keeps
 * the default value. Called with no argument it returns the default light theme.
 *
 * @example
 * const theme = lightTheme({
 *   background: { button: { primary: { default: '#1e3b25', pressed: '#264e30' } } },
 *   brand: { primary: '#1e3b25' },
 * })
 */
export function lightTheme(override?: FunkitTokensOverride): FunkitTheme {
  return toRestyleTheme(deepMerge(lightTokens, override))
}

/**
 * Build a dark {@link FunkitTheme}, deep-merging an optional brand override onto
 * the SDK's `darkTokens`. See {@link lightTheme}. For a dark-only brand, call
 * this once and reuse the theme for both `light` and `dark`.
 */
export function darkTheme(override?: FunkitTokensOverride): FunkitTheme {
  return toRestyleTheme(deepMerge(darkTokens, override))
}
